import { Box, Button, Card, CardContent, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type {
    EAvailabilityInputAs,
    EAvailabilitySelectionType,
    IAvailabilityWeekDTO,
} from '@sharedInterfaces/IAvailabilityMonth';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import AvailabilityMonth from '@src/Objects/AvailabilityMonth';
import type { AppState } from '@store/store';
import { Calendar, FileText, Phone, Settings, Target, TrendingUp, Users, Zap } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MonthBox } from './AvailabilitySettings/MonthBox';
import { ProfileTabHeader } from './ProfileTabHeader';

const isMoreThanAMonthAgo = (lastSkillUpdate: Date): boolean => {
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    return lastSkillUpdate < oneMonthAgo;
};

export function ForecastTab(): JSX.Element {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).availability;
    const companyId = useSelector((state: AppState) => state.company.companyId);
    const lastSkillUpdate = useSelector((state: AppState) => state.employeeSettings.lastSkillUpdate);

    const [months, setMonths] = useState<AvailabilityMonth[]>([]);
    const needSkillUpdate = useMemo(() => isMoreThanAMonthAgo(lastSkillUpdate), [lastSkillUpdate]);

    useEffect(() => {
        if (companyId) {
            AvailabilityMonth.getAvailabilityMonths(setMonths);
        }
    }, [companyId]);

    const handleAvailabilityChange = (
        id: string,
        weekArray: IAvailabilityWeekDTO[],
        type: EAvailabilitySelectionType,
        inputAs: EAvailabilityInputAs,
    ) => {
        setMonths((prevMonths) =>
            prevMonths.map((m) => {
                if (m.id === id) {
                    m.selectionType = type;
                    m.inputAs = inputAs;
                }
                return new AvailabilityMonth({ ...m });
            }),
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ProfileTabHeader
                title={langStrings.forecastPlanning}
                subtitle="Verfügbarkeitsplanung und Jahresübersicht"
            />

            <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                            },
                        }}
                    >
                        {months.map((month) => (
                            <MonthBox
                                key={month.id}
                                setMonths={setMonths}
                                month={month}
                                setAvailability={handleAvailabilityChange}
                            />
                        ))}

                        {/* Jahres-Statistik Widget - feste Position */}
                        {months.length > 0 ? (
                            <YearStatsWidget months={months} />
                        ) : (
                            <Box /> // Platzhalter für stabiles Layout
                        )}

                        {/* Info Widget - feste Position */}
                        {needSkillUpdate && <InfoWidget message={langStrings.noSkillUpdateOneMonth} />}
                    </Box>
                </CardContent>
            </Card>

            {/* Additional Widgets Section */}
            <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Weitere Informationen
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                lg: 'repeat(4, 1fr)',
                            },
                        }}
                    >
                        <NextAppointmentsWidget />
                        <AvailabilityTrendWidget months={months} />
                        <TeamAvailabilityWidget />
                        <QuickActionsWidget />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

interface YearStatsWidgetProps {
    months: AvailabilityMonth[];
}

function YearStatsWidget({ months }: YearStatsWidgetProps) {
    const theme = useTheme();

    const stats = useMemo(() => {
        const totalWorkDays = months.reduce((sum, m) => sum + m.workDayCount - m.holidayCount, 0);
        const totalPlannedDays = months.reduce((sum, m) => sum + m.plannedDayCount, 0);
        const totalHolidayDays = months.reduce((sum, m) => sum + m.holidayCount, 0);
        const avgAvailability = totalWorkDays > 0 ? Math.round((1 - totalPlannedDays / totalWorkDays) * 100) : 0;

        return {
            totalWorkDays,
            totalPlannedDays,
            totalHolidayDays,
            avgAvailability,
        };
    }, [months]);

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                background: alpha(theme.palette.primary.main, 0.05),
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Target size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Jahresübersicht {new Date().getFullYear()}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Ø Verfügbarkeit
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                            {stats.avgAvailability}%
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Geplante Tage
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {Math.round(stats.totalPlannedDays)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Urlaubstage
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {stats.totalHolidayDays}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 1,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            Trend
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                            +5%
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

function NextAppointmentsWidget() {
    const theme = useTheme();

    const appointments = useMemo(
        () => [
            { date: '15.01', title: 'Projektmeeting' },
            { date: '22.01', title: 'Urlaubsantrag' },
            { date: '28.01', title: 'Review Call' },
        ],
        [],
    );

    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                background: 'transparent',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Calendar size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Anstehende Termine
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {appointments.map((appointment) => (
                        <Typography key={appointment.date} variant="body2" sx={{ opacity: 0.8 }}>
                            {appointment.date} - {appointment.title}
                        </Typography>
                    ))}
                    <Typography variant="caption" sx={{ color: theme.palette.primary.main, mt: 1, cursor: 'pointer' }}>
                        → Alle Termine
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

interface AvailabilityTrendWidgetProps {
    months: AvailabilityMonth[];
}

function AvailabilityTrendWidget({ months }: AvailabilityTrendWidgetProps) {
    const theme = useTheme();

    const trendData = useMemo(
        () =>
            months.slice(0, 4).map((month) => {
                const availability =
                    100 -
                    Math.round(
                        ((month.plannedDayCount + month.absenceDayCount + month.holidayCount) / month.workDayCount) *
                            100,
                    );
                return {
                    id: month.id,
                    title: month.LocalTitle.slice(0, 3),
                    availability: Math.max(0, availability),
                };
            }),
        [months],
    );

    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                background: 'transparent',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Verfügbarkeitstrend
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {trendData.map((item) => (
                        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 40, opacity: 0.8 }}>
                                {item.title}
                            </Typography>
                            <Box
                                sx={{
                                    flex: 1,
                                    height: 8,
                                    bgcolor: alpha(theme.palette.divider, 0.2),
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${item.availability}%`,
                                        height: '100%',
                                        bgcolor: theme.palette.primary.main,
                                        borderRadius: 1,
                                    }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right', fontWeight: 500 }}>
                                {item.availability}%
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
}

function QuickActionsWidget() {
    const theme = useTheme();

    const actions = useMemo(
        () => [
            { icon: FileText, label: 'Urlaub beantragen' },
            { icon: TrendingUp, label: 'Bericht exportieren' },
            { icon: Settings, label: 'Einstellungen' },
            { icon: Phone, label: 'Support kontaktieren' },
        ],
        [],
    );

    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                background: 'transparent',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Zap size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Schnellaktionen
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {actions.map((action) => {
                        const IconComponent = action.icon;
                        return (
                            <Button
                                key={action.label}
                                variant="outlined"
                                size="small"
                                startIcon={<IconComponent size={16} />}
                                fullWidth
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                            >
                                {action.label}
                            </Button>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
}

function TeamAvailabilityWidget() {
    const theme = useTheme();

    const teamMembers = useMemo(
        () => [
            { name: 'Max Müller', availability: 85, color: theme.palette.success.main },
            { name: 'Anna Schmidt', availability: 65, color: theme.palette.warning.main },
            { name: 'Tom Weber', availability: 45, color: theme.palette.error.main },
        ],
        [theme],
    );

    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                background: 'transparent',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Users size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Team Verfügbarkeit
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {teamMembers.map((member) => (
                        <Box
                            key={member.name}
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                {member.name}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: member.color }}>
                                {member.availability}%
                            </Typography>
                        </Box>
                    ))}
                    <Typography variant="caption" sx={{ color: theme.palette.primary.main, mt: 1, cursor: 'pointer' }}>
                        → Team Details
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

interface InfoWidgetProps {
    message: string;
}

function InfoWidget({ message }: InfoWidgetProps) {
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                background: alpha(theme.palette.info.main, 0.1),
                '&:hover': {
                    bgcolor: alpha(theme.palette.info.main, 0.15),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                        sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: theme.palette.info.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.info.contrastText,
                            fontSize: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        !
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.info.main }}>
                        Hinweis
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {message}
                </Typography>
            </CardContent>
        </Card>
    );
}
